import { BehaviorData } from "../behaviordata";

/** Type identifier for AudioClip behaviors */
export const AUDIO_CLIP_TYPE = "AudioClip" as const;

/**
 * Standalone audio clip playback. The SceneObject's `src` field carries the
 * media URL (mp3 in v1; any format the browser's HTMLAudioElement decodes
 * works once added to AUDIO_EXTENSIONS).
 *
 * Audio plays through the element's default sink by default. Add an
 * `AudioSource` behavior on the same SceneObject to route the audio through
 * the Web Audio graph for volume, distance attenuation, and 3D positioning.
 *
 * @action play - Start playback
 * @action pause - Pause playback
 * @action stop - Stop and rewind to the beginning
 * @action seek - Seek to a time in seconds. Args: (time, mode?) where mode is "absolute" (default) or "relative".
 * @action setVolume - Set element volume, 0-1 (takes volume argument). For spatial control use AudioSource.
 */
export interface AudioClipData extends BehaviorData {
    "@type": typeof AUDIO_CLIP_TYPE;

    /**
     * Whether to start playback automatically when the scene enters play mode.
     * @default true
     */
    playOnStart?: boolean;

    /**
     * Whether to loop playback.
     * @default true
     */
    loop?: boolean;

    /**
     * Timeline time, in seconds, at which the clip's first sample sounds
     * (after Opus pre-skip). A driving Timeline's `seek(t)` places the media
     * at `t − startAt` (clamped at 0); for `t < startAt` the clip is silent
     * and starts by itself when the clip's time reaches `startAt` while
     * playing. Negative means the audio began before t = 0, so t = 0 lands
     * `−startAt` seconds into the media. The same offset applies without a
     * Timeline: the clip's own `play()`/`playOnStart` count from t = 0.
     * @default 0
     * @step 0.001
     */
    startAt?: number;
}
